import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatMoney } from "@/lib/api";
import { CART } from "@/constants/testIds";

export default function CartDrawer() {
  const { items, updateQty, removeItem, totals, drawerOpen, setDrawerOpen } = useCart();
  const nav = useNavigate();

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent
        side="right"
        data-testid={CART.drawer}
        className="w-full sm:max-w-md p-0 flex flex-col bg-white"
      >
        <SheetHeader className="px-6 py-5 border-b border-slate-200">
          <SheetTitle className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-emerald-900" />
            Your Cart ({totals.count})
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div data-testid={CART.emptyState} className="p-10 text-center">
              <ShoppingBag className="w-10 h-10 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-6">Your cart is empty.</p>
              <Link
                to="/shop"
                onClick={() => setDrawerOpen(false)}
                className="inline-block px-5 py-2.5 bg-emerald-900 text-white text-sm rounded-sm hover:bg-emerald-800"
              >
                Browse products
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((item) => (
                <li
                  key={item.product_id}
                  data-testid={CART.item(item.product_id)}
                  className="flex gap-4 p-5"
                >
                  <div className="w-20 h-20 bg-slate-50 flex-shrink-0 overflow-hidden rounded-sm border border-slate-200">
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-900 line-clamp-2 leading-snug">
                      {item.name}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">SKU {item.sku}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="inline-flex items-center border border-slate-300 rounded-sm">
                        <button
                          data-testid={CART.qtyMinus(item.product_id)}
                          onClick={() => updateQty(item.product_id, item.quantity - 1)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-slate-100"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-sm">{item.quantity}</span>
                        <button
                          data-testid={CART.qtyPlus(item.product_id)}
                          onClick={() => updateQty(item.product_id, item.quantity + 1)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-slate-100"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="text-right">
                        <p className="font-display font-bold text-slate-900">
                          {formatMoney(item.price * item.quantity)}
                        </p>
                        <button
                          data-testid={CART.remove(item.product_id)}
                          onClick={() => removeItem(item.product_id)}
                          className="text-xs text-slate-400 hover:text-red-600 flex items-center gap-1 mt-1"
                        >
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-slate-200 p-6 space-y-4 bg-slate-50">
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal</span>
                <span data-testid={CART.subtotal} className="font-medium text-slate-900">
                  {formatMoney(totals.subtotal)}
                </span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Shipping</span>
                <span>{totals.shipping === 0 ? "FREE" : formatMoney(totals.shipping)}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Tax (8%)</span>
                <span>{formatMoney(totals.tax)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-slate-200 text-slate-900 font-display font-bold text-base">
                <span>Total</span>
                <span>{formatMoney(totals.total)}</span>
              </div>
            </div>
            <button
              data-testid={CART.checkoutBtn}
              onClick={() => {
                setDrawerOpen(false);
                nav("/checkout");
              }}
              className="w-full py-3.5 bg-emerald-900 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800 transition-colors"
            >
              Proceed to secure checkout
            </button>
            <p className="text-[11px] text-slate-500 text-center">
              Distributor drop-ships direct. No-touch freight.
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
